import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    API_ADD_LOAN_REPAYMENT,
    API_BRANCHES,
    API_CENTERS,
    API_GET_CLIENTS_FOR_REPAYMENT,
    API_GET_REPAYMENT_PAID,
    API_REPAYMENT_STAFF
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { confirmAction } from '../utils/confirmAction';

const getCurrentDate = () => new Date().toLocaleDateString('en-CA');

const initialFormData = {
    branch_id: "",
    center_id: "",
    client_id: "",
    installment_no: "",
    installment_amt: "",
    repayment_paid_by_staff: "",
    is_closure: 0,
    created_at: getCurrentDate(),
};

const LoanRepayment = () => {
    const { logout } = useAuth();
    const [formData, setFormData] = useState(initialFormData);
    const [clients, setClients] = useState([]);
    const [centerList, setCenterList] = useState([]);
    const [branches, setBranches] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [lastInstallmentNo, setLastInstallmentNo] = useState(0);
    const [clientLoadError, setClientLoadError] = useState('');
    const centerRequestId = useRef(0);
    const clientRequestId = useRef(0);
    const token = localStorage.getItem('token');
    const selectedRepaymentClient = clients.find((client) => String(client.id) === String(formData.client_id));
    const numbers = Array.from({ length: Number(selectedRepaymentClient?.total_installments || 26) }, (_, index) => index + 1);

    const staffEmployees = employeeList;

    const getEmployees = useCallback(async (branchId = '') => {
        try {
            const response = await fetch(API_REPAYMENT_STAFF, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(branchId ? { branch_id: branchId } : {}),
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const responseData = await response.json();
            setEmployeeList(responseData.data || []);
        } catch (error) {
            setEmployeeList([]);
        }
    }, [logout, token]);

    const getCentersByBranch = async (branchId) => {
        const requestId = ++centerRequestId.current;
        if (!branchId) {
            setCenterList([]);
            return;
        }

        try {
            const response = await fetch(API_CENTERS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ is_all: false, branch_id: branchId })
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const responseData = await response.json();
            if (requestId === centerRequestId.current) setCenterList(responseData.data || []);
        } catch (error) {
            setCenterList([]);
        }
    };

    const resetForm = () => {
        setFormData({ ...initialFormData, created_at: getCurrentDate() });
        setClients([]);
        setCenterList([]);
        setEmployeeList([]);
        setLastInstallmentNo(0);
        setClientLoadError('');
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;

        if (name === "client_id") {
            const selectedClient = clients.find((client) => String(client.id) === value);
            if (!selectedClient) {
                setLastInstallmentNo(0);
                setFormData((prevData) => ({
                    ...prevData,
                    client_id: '',
                    installment_amt: '',
                    installment_no: '',
                }));
                return;
            }

            const remainingPrincipalAmt = Number(selectedClient.remaining_principal_amt || 0);
            const cycleInstallmentAmt = Number(selectedClient.cycle_installment_amt || 0);
            const installmentAmt = formData.is_closure
                ? remainingPrincipalAmt
                : (remainingPrincipalAmt > 0 ? cycleInstallmentAmt : remainingPrincipalAmt);

            setFormData((prevData) => ({
                ...prevData,
                client_id: selectedClient.id,
                installment_amt: installmentAmt,
                installment_no: formData.is_closure ? '' : String(Number(selectedClient.last_installment_no || 0) + 1),
            }));
            return;
        }

        if (name === 'is_closure') {
            const selectedClient = clients.find((client) => String(client.id) === String(formData.client_id));
            if (selectedClient) {
                const remainingPrincipalAmt = Number(selectedClient.remaining_principal_amt || 0);
                const cycleInstallmentAmt = Number(selectedClient.cycle_installment_amt || 0);
                const nextIsClosure = checked ? 1 : 0;
                const installmentAmt = nextIsClosure
                    ? remainingPrincipalAmt
                    : (remainingPrincipalAmt > 0 ? cycleInstallmentAmt : remainingPrincipalAmt);

                setFormData((prevData) => ({
                    ...prevData,
                    is_closure: nextIsClosure,
                    installment_amt: installmentAmt,
                }));
            } else {
                setFormData((prevData) => ({
                    ...prevData,
                    is_closure: checked ? 1 : 0,
                }));
            }
            return;
        }

        if (name === 'branch_id') {
            clientRequestId.current += 1;
            if (!value) {
                resetForm();
                return;
            }

            void getCentersByBranch(value);
            void getEmployees(value);
            setClients([]);
            setClientLoadError('');
            setLastInstallmentNo(0);
            setFormData((prevData) => ({
                ...prevData,
                branch_id: value,
                center_id: "",
                client_id: "",
                installment_no: "",
                installment_amt: "",
                repayment_paid_by_staff: "",
            }));
            return;
        }

        if (name === 'center_id' && !value) {
            setClients([]);
            setLastInstallmentNo(0);
            setFormData((prevData) => ({
                ...prevData,
                center_id: "",
                client_id: "",
                installment_no: "",
                installment_amt: "",
            }));
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!confirmAction('submit this repayment for approval')) return;
        try {
            const response = await fetch(API_ADD_LOAN_REPAYMENT, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const result = await response.json();
            if (response.ok && result?.status) {
                resetForm();
                alert(result.message);
            } else {
                alert(result?.message || result?.error || 'Unable to save the repayment.');
            }
        } catch (error) {
            alert("An error occurred. Please try again.");
        }
    };

    useEffect(() => {
        const loadMasterData = async () => {
            try {
                const response = await fetch(API_BRANCHES, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const responseData = await response.json();
                setBranches(responseData.data || []);
            } catch (error) {
                setBranches([]);
            }

            await getEmployees();
        };

        void loadMasterData();
    }, [getEmployees, logout, token]);

    useEffect(() => {
        const loadClients = async () => {
            const requestId = ++clientRequestId.current;
            if (!formData.center_id) {
                setClients([]);
                setClientLoadError('');
                return;
            }

            try {
                const response = await fetch(API_GET_CLIENTS_FOR_REPAYMENT, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        branch_id: formData.branch_id,
                        center_id: formData.center_id,
                    }),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const result = await response.json();
                if (!response.ok || !result?.status) {
                    throw new Error(result?.message || result?.error || 'Unable to load clients for repayment.');
                }
                if (requestId === clientRequestId.current) {
                    setClients(result.data || []);
                    setClientLoadError(result.data?.length ? '' : 'No active loan clients without a pending request are available for this center.');
                }
            } catch (error) {
                if (requestId === clientRequestId.current) {
                    setClients([]);
                    setClientLoadError(error.message || 'Unable to load clients for repayment.');
                }
            }
        };

        void loadClients();
    }, [formData.branch_id, formData.center_id, logout, token]);

    useEffect(() => {
        const loadInstallmentPaid = async () => {
            if (!formData.client_id || !formData.center_id || !formData.branch_id) {
                setLastInstallmentNo(0);
                return;
            }

            try {
                const response = await fetch(API_GET_REPAYMENT_PAID, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        branch_id: formData.branch_id,
                        center_id: formData.center_id,
                        client_id: formData.client_id,
                    }),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const result = await response.json();
                const paidInstallmentNo = Number(result?.data?.installment_no || 0);
                setLastInstallmentNo(paidInstallmentNo);
                setFormData((current) => (
                    current.is_closure ? current : { ...current, installment_no: String(paidInstallmentNo + 1) }
                ));
            } catch (error) {
                alert("An error occurred. Please try again.");
            }
        };

        void loadInstallmentPaid();
    }, [formData.branch_id, formData.center_id, formData.client_id, logout, token]);

    return (
        <div className="container my-5">
            <h2 className="text-center">Loan Repayment Form</h2>
            <form onSubmit={handleSubmit} className="shadow p-4 rounded bg-light">
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-check mb-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="is_closure"
                                name="is_closure"
                                checked={formData.is_closure === 1}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="is_closure" style={{ marginLeft: "10px", paddingTop: "3px" }}>
                                Is full settlement?
                            </label>
                        </div>
                        <div>
                            <small className={`text-${formData.is_closure ? 'success' : 'secondary'}`}>
                                {formData.is_closure ? "Marked as full settlement." : "Not marked for full settlement."}
                            </small>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="branch_id" className="form-label">Branch Name</label>
                            <select
                                id="branch_id"
                                className="form-control"
                                name="branch_id"
                                value={formData.branch_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Branch</option>
                                {branches.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branch_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="center_id" className="form-label">Center Name</label>
                            <select
                                id="center_id"
                                className="form-control"
                                name="center_id"
                                value={formData.center_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Center</option>
                                {centerList.map((center) => (
                                    <option key={center.id} value={center.id}>
                                        {center.center_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="client_id" className="form-label">Client Name</label>
                            <select
                                id="client_id"
                                className="form-control"
                                name="client_id"
                                value={formData.client_id}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Client</option>
                                {clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.client_no} - {client.client_name}
                                    </option>
                                ))}
                            </select>
                            {clientLoadError ? <small className="text-danger d-block mt-1">{clientLoadError}</small> : null}
                        </div>
                    </div>

                    {formData.is_closure === 0 && (
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label htmlFor="installment_no" className="form-label">Installment No (1-26)</label>
                                <select
                                    id="installment_no"
                                    className="form-control"
                                    name="installment_no"
                                    value={formData.installment_no}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Installment</option>
                                    {numbers
                                        .filter((number) => lastInstallmentNo === 0 || number > lastInstallmentNo)
                                        .map((number) => (
                                            <option key={number} value={number}>
                                                {number}
                                            </option>
                                        ))}
                                </select>
                                {formData.client_id ? <small className="text-muted">Next installment: {lastInstallmentNo + 1}</small> : null}
                            </div>
                        </div>
                    )}
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="installment_amt" className="form-label">Installment Amount</label>
                            <input
                                type="number"
                                id="installAmt"
                                name="installment_amt"
                                className="form-control"
                                value={formData.installment_amt}
                                onChange={handleChange}
                                readOnly
                                required
                            />
                            <small className="text-muted">Amount is calculated from the selected active loan.</small>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="repayment_paid_by_staff" className="form-label">Repayment Paid by Staff</label>
                            <select
                                className="form-control"
                                name="repayment_paid_by_staff"
                                value={formData.repayment_paid_by_staff}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select staff</option>
                                {staffEmployees.map((employee) => (
                                    <option key={employee.id} value={employee.id}>
                                        {employee.emp_generate_id} - {employee.emp_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label htmlFor="created_at" className="form-label">Repayment Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="created_at"
                                value={formData.created_at}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="text-center">
                    <button type="submit" className="btn btn-primary">Submit</button>
                </div>
            </form>
        </div>
    );
};

export default LoanRepayment;
